import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IBaseResponse } from '@carm/_utils/models';
import { RoutesConst } from '@carm/shared/constants/const-routes';
import { HttpParams } from '@angular/common/http';
import { LoaderSpinnerTypes } from '@carm/_core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { IGetDraftsResponse, IDraftResponse, IDeleteDraftRequest } from '../models/api';
import { ITempImpVendorListResponse, ITempImpGoodListResponse, ITempImpDraftHeaderResponse, ITempImpDraftHeaderRequest,
  ITempImpVendorResponse, ITempImpVendor, ITempImpGoodBase, ITempImpDraftUpdateRequest } from '../models/api/temporary-importation-api.model';
import { TempImpStep, IStepState, CADType, IStep } from '../models/common';
import { ICadDraftUiContent } from '../models/declarations.model';
import { StepperService } from './stepper.service';
@Injectable()
export class TemporaryImportationDraftService extends BaseApiService {

  get temporaryImportationURL(): string {
    return `${this.endPointBP}/cad/temporary-importation`;
  }

  private readonly tempImpVendorCountChangeSubject =
    new BehaviorSubject<boolean>(false);
  private readonly tempImpGoodCountChangeSubject = new BehaviorSubject<boolean>(
    false
  );
  private readonly vendorsListChangeSubject =
    new BehaviorSubject<ITempImpVendorListResponse>(null);
  private readonly goodsListChangeSubject = new BehaviorSubject<
    Map<string, ITempImpGoodListResponse>
  >(new Map<string, ITempImpGoodListResponse>());
  private readonly draftHeaderChangeSubject =
    new BehaviorSubject<ITempImpDraftHeaderResponse>(null);

  constructor(
    protected injector: Injector,
    private stepperService: StepperService
  ) {
    super(injector);
  }

  public get onTempImpVendorCountChangeChange$(): Observable<boolean> {
    return this.tempImpVendorCountChangeSubject.asObservable();
  }

  public get onTempImpGoodCountChangeSubjectChange$(): Observable<boolean> {
    return this.tempImpGoodCountChangeSubject.asObservable();
  }

  public get onGetDraftHeaderChange$(): Observable<ITempImpDraftHeaderResponse> {
    return this.draftHeaderChangeSubject.asObservable();
  }

  public get draftHeader(): ITempImpDraftHeaderResponse {
    return this.draftHeaderChangeSubject.getValue();
  }

  public get onVendorsListChanged$(): Observable<ITempImpVendorListResponse> {
    return this.vendorsListChangeSubject.asObservable();
  }

  public get onVendorsListSnapshot(): ITempImpVendorListResponse {
    return this.vendorsListChangeSubject.getValue();
  }

  public get goodsListChanged$(): Observable<
    Map<string, ITempImpGoodListResponse>
  > {
    return this.goodsListChangeSubject.asObservable();
  }

  public get goodsListSnapshot(): Map<string, ITempImpGoodListResponse> {
    return this.goodsListChangeSubject.getValue();
  }


  getTemporaryImportationDrafts(
    // minDate: Date,
    // maxDate: Date,
    currentPage: number,
    pageSize: number,
    sortBy: string,
    filterField: string,
    filterValue: string
  ): Observable<IGetDraftsResponse> {
    const endPoint = `${this.temporaryImportationURL}/drafts`;
    const params = new HttpParams()
      .set('pageSize', pageSize.toString())
      .set('currentPage', currentPage.toString())
      .set('sort', sortBy)
      .set('filterField', filterField)
      .set('filterValue', filterValue);
    return this.httpAuthGet<IGetDraftsResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  public createTempImpDraft(
    draft: ITempImpDraftHeaderRequest
  ): Observable<ITempImpDraftHeaderResponse> {
    const draftUiContent: ICadDraftUiContent = {
      stepStates: [
        { id: TempImpStep.Overview, isComplete: true },
        { id: TempImpStep.GeneralDetails, isComplete: false },
        { id: TempImpStep.Destination, isComplete: false },
        { id: TempImpStep.Dates, isComplete: false },
        { id: TempImpStep.VendorsGoods, isComplete: false },
        { id: TempImpStep.Review, isComplete: false },
      ],
    };

    const draftUiContentJson = JSON.stringify(draftUiContent);
    draft.draftUiContent = draftUiContentJson;

    const endPoint = `${this.temporaryImportationURL}/draft`;
    return this.httpAuthPost<ITempImpDraftHeaderResponse>(endPoint, draft);
  }

  public getDraft(uid: string): Observable<ITempImpDraftHeaderResponse> {
    const endPoint = `${this.temporaryImportationURL}/draft/${uid}`;
    return this.httpAuthGet<ITempImpDraftHeaderResponse>(endPoint, null).pipe(
      tap(draftResponse => {
        if (draftResponse) {
          this.draftHeaderChangeSubject.next(draftResponse);
        }
      })
    );
  }

  updateTempImpHeaderDraft(
    draft: ITempImpDraftHeaderRequest,
    stepState: IStepState
  ): Observable<ITempImpDraftHeaderResponse> {
    if (stepState) {
      let draftUiContent: ICadDraftUiContent;

      if (draft.draftUiContent) {
        draftUiContent = JSON.parse(draft.draftUiContent);

        // if json gets corrupted, or unknown state
        if(!draftUiContent?.stepStates?.some(s => s.id === stepState.id)){
          draftUiContent = {
            stepStates: this.stepperService
            .getSteps(CADType.TEMPORARY_IMPORTATION)
            .map(step => ({ id: step.id, isComplete: false } as IStepState))
          };
        }
      } else {
        // if draftUiContent was removed, create a new one
        draftUiContent = {
          stepStates: this.stepperService
            .getSteps(CADType.TEMPORARY_IMPORTATION)
            .map(step => ({ id: step.id, isComplete: false } as IStepState)),
        };
      }

      const currentState = draftUiContent.stepStates.find(
        ss => ss.id === stepState.id
      );

      if (currentState) {
        currentState.isComplete = stepState.isComplete;
      } else {
        draftUiContent.stepStates.push(stepState); // a new step not part of draft?
      }

      draft.draftUiContent = JSON.stringify(draftUiContent);
    }

    const endPoint = `${this.temporaryImportationURL}/draft/${draft.headerGuid}`;
    return this.httpAuthPut<ITempImpDraftHeaderResponse>(endPoint, draft).pipe(
      tap(draftResponse => {
        if (draftResponse) {
          draftResponse.draftUiContent =
            draft.draftUiContent || draft.draftUiContent; //bug workaround. api sometimes returns null draftUiContent... take up request one as backup

          this.draftHeaderChangeSubject.next(draftResponse);
        }
      })
    );
  }

  deleteDraft(draftUid: string, etag: string): Observable<IDraftResponse> {
    const deleteRequest: IDeleteDraftRequest = {
      headerGuid: draftUid,
      etag: etag,
    };

    const endPoint = `${this.temporaryImportationURL}/draft/${draftUid}`;
    return this.httpAuthDelete<IDraftResponse>(endPoint, null, deleteRequest);
  }

  public createVendor(draftUid: string): Observable<ITempImpVendorResponse> {
    const endPoint = `${this.temporaryImportationURL}/draft/${draftUid}/vendor`;
    return this.httpAuthPost<ITempImpVendorResponse>(endPoint, {}).pipe(
      tap(_ => {
        this.tempImpVendorCountChangeSubject.next(true);
      })
    );
  }

  public deleteVendor(
    draftUid: string,
    vendor: ITempImpVendor
  ): Observable<IBaseResponse> {
    const endPoint = `${this.temporaryImportationURL}/draft/${draftUid}/vendor/${vendor.subHeaderGuid}`;
    return this.httpAuthDelete<IBaseResponse>(endPoint, null, vendor).pipe(
      tap(_ => {
        this.tempImpVendorCountChangeSubject.next(true);
      })
    );
  }

  public getVendor(
    draftUid: string,
    vendorUid: string
  ): Observable<ITempImpVendorResponse> {
    const endPoint = `${this.temporaryImportationURL}/draft/${draftUid}/vendor/${vendorUid}`;
    return this.httpAuthGet<ITempImpVendorResponse>(endPoint, null);
  }

  public updateVendor(
    draftUid: string,
    vendorRequest: ITempImpVendor
  ): Observable<ITempImpVendorResponse> {
    const endPoint = `${this.temporaryImportationURL}/draft/${draftUid}/vendor/${vendorRequest.subHeaderGuid}`;
    return this.httpAuthPut<ITempImpVendorResponse>(endPoint, vendorRequest);
  }

  public getVendorList(
    draftUid: string,
    currentPage: number,
    pageSize: number,
    sortBy: string = 'VENDOR_LINE_NUMBER_ASC'
  ): Observable<ITempImpVendorListResponse> {
    const params = new HttpParams()
      .set('pageSize', pageSize ? pageSize.toString() : '10')
      .set('currentPage', currentPage ? currentPage.toString() : '1')
      .set('sort', sortBy);
    const endPoint = `${this.temporaryImportationURL}/draft/${draftUid}/vendors`;
    return this.httpAuthGet<ITempImpVendorListResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid).pipe(
      tap(vendorsResponse => {
        if (vendorsResponse) {
          this.vendorsListChangeSubject.next(vendorsResponse);
        }
      })
    );
  }

  public getGoodsList(
    draftUid: string,
    vendorUid: string,
    loaderSpinnerType: LoaderSpinnerTypes
  ): Observable<ITempImpGoodListResponse> {
    const maxPageSize = 100;
    const vendorUrl = `${this.temporaryImportationURL}/draft/${draftUid}/vendor/${vendorUid}`;
    const endPoint = `${vendorUrl}/goods?pageSize=${maxPageSize}`;
    return this.httpAuthGet<ITempImpGoodListResponse>(endPoint, null, loaderSpinnerType).pipe(
      tap(goodsResponse => {
        if (goodsResponse) {
          const snapshot = this.goodsListSnapshot;
          snapshot.set(vendorUid, goodsResponse);
          this.goodsListChangeSubject.next(snapshot);
        }
      })
    );
  }

  public getPaginatedGoodsList(
    draftUid: string,
    vendorUid: string,
    currentPage: number,
    pageSize: number,
    loaderSpinnerType: LoaderSpinnerTypes
  ): Observable<ITempImpGoodListResponse> {
    const vendorUrl = `${this.temporaryImportationURL}/draft/${draftUid}/vendor/${vendorUid}`;
    const endPoint = `${vendorUrl}/goods?pageSize=${pageSize}&currentPage=${currentPage}`;
    return this.httpAuthGet<ITempImpGoodListResponse>(endPoint, null, loaderSpinnerType).pipe(
      tap(goodsResponse => {
        if (goodsResponse) {
          const snapshot = this.goodsListSnapshot;
          snapshot.set(vendorUid, goodsResponse);
          this.goodsListChangeSubject.next(snapshot);
        }
      })
    );
  }


  public createGood(
    draftUid: string,
    vendorUid: string
  ): Observable<ITempImpGoodBase> {
    const endPoint = `${this.temporaryImportationURL}/draft/${draftUid}/vendor/${vendorUid}/good`;
    return this.httpAuthPost<ITempImpGoodBase>(endPoint, {}).pipe(
      tap(_ => {
        this.tempImpGoodCountChangeSubject.next(true);
      })
    );
  }

  public updateGood(
    draftUid: string,
    vendorUid: string,
    good: ITempImpGoodBase
  ): Observable<any> {
    const endPoint = `${this.temporaryImportationURL}/draft/${draftUid}/vendor/${vendorUid}/good/${good.commodityGuid}`;
    return this.httpAuthPut<any>(endPoint, good);
  }

  public getGood(
    draftUid: string,
    vendorUid: string,
    goodUid: string
  ): Observable<ITempImpGoodBase> {
    const endPoint = `${this.temporaryImportationURL}/draft/${draftUid}/vendor/${vendorUid}/good/${goodUid}`;
    return this.httpAuthGet<ITempImpGoodBase>(endPoint, null);
  }

  public deleteGood(
    draftUid: string,
    vendorUid: string,
    good: ITempImpGoodBase
  ): Observable<IBaseResponse> {
    const endPoint = `${this.temporaryImportationURL}/draft/${draftUid}/vendor/${vendorUid}/good/${good.commodityGuid}`;
    return this.httpAuthDelete<IBaseResponse>(endPoint, null, good).pipe(
      tap(_ => {
        this.tempImpGoodCountChangeSubject.next(true);
      })
    );
  }

  public validate(draftUid: string) {
    const endPoint = `${this.temporaryImportationURL}/draft/validate/${draftUid}`;
    return this.httpAuthPost<any>(endPoint, {});
  }

  public submit(draftUid: string): Observable<ITempImpDraftHeaderResponse> {
    const endPoint = `${this.temporaryImportationURL}/draft/submit/${draftUid}`;

    return this.httpAuthPost<any>(endPoint, {}).pipe(
      tap(_ => {
        this.draftHeaderChangeSubject.next(null); // clean slate for next
      })
    );
  }

  public updateSubmittedTempImport(
    draftUid: string,
    draft: ITempImpDraftUpdateRequest
  ): Observable<ITempImpDraftHeaderResponse> {
    const endPoint = `${this.temporaryImportationURL}/draft/set-extension-date/${draftUid}`;
    return this.httpAuthPut<any>(endPoint, draft);
  }

  getLastEditRoute(draftUiContentRaw: string, allSteps: IStep[]): string {
    if (!draftUiContentRaw) {
      return RoutesConst.Declarations_Temporary_importation_review_info_rel;
    }

    const completedStates = (
      JSON.parse(draftUiContentRaw) as ICadDraftUiContent
    )?.stepStates?.filter(s => s !== null && s.isComplete);
    const allStepsSorted = [...allSteps].sort((a, b) => b.order - a.order);

    if (completedStates?.length === 0) {
      return RoutesConst.Declarations_Temporary_importation_review_info_rel;
    }

    for (let i = 0; i < allStepsSorted.length; i++) {
      if (
        completedStates.find(s => allStepsSorted[i].id === s.id)?.isComplete
      ) {
        return allStepsSorted[i - 1].routeName;
      }
    }
  }
}
